import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  Avatar, Banner, Button, Card, Chip, DataTable, Dialog, List, ListItem, Select, TextField, TopAppBar,
  useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { localeIntl, t } from '../lib/lingua';
import { quandoRelativo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { useMe, usePermessi } from '../session';
import { Copy } from '../icons';
import { Anello, CaricoSezione } from '../brand';

type Ruolo = 'owner' | 'admin' | 'member' | 'viewer' | 'restricted';

const RUOLI: Ruolo[] = ['owner', 'admin', 'member', 'viewer', 'restricted'];

// Nomi e spiegazioni si calcolano a ogni render e non all'import: devono
// seguire la lingua di chi guarda, che può cambiare dopo.

/** Il nome del ruolo; uno che il pannello non conosce resta com'è. */
function nomeRuolo(r: string): string {
  const nomi: Record<Ruolo, string> = {
    owner: t('Proprietario'),
    admin: t('Amministratore'),
    member: t('Membro'),
    viewer: t('Sola lettura'),
    restricted: t('Accesso limitato'),
  };
  return nomi[r as Ruolo] ?? r;
}

/** Cosa può fare ciascun ruolo, con parole di chi sceglie. */
function spiegaRuolo(r: Ruolo): string {
  const spiega: Record<Ruolo, string> = {
    owner: t('Tutto, compreso eliminare l’organizzazione.'),
    admin: t('Tutto, tranne eliminare l’organizzazione.'),
    member: t('Crea istanze, fa backup e ripristini.'),
    viewer: t('Guarda, non tocca.'),
    restricted: t('Vede solo ciò che gli è stato assegnato.'),
  };
  return spiega[r];
}

const opzioniRuolo = () => RUOLI.map((r) => ({ value: r, label: nomeRuolo(r) }));

export function MembersPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/members' });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const me = useMe();
  // Le persone sono dell'owner (blocco 33). Il superadmin riceve da `/me` ogni
  // permesso, quindi il pannello non gli nasconde controlli che l'API accetta.
  const amministra = usePermessi(slug)('member.manage');
  const puoInvitare = usePermessi(slug)('member.invite');

  const membri = useQuery({
    queryKey: ['members', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/members', { params: { path: { slug } } })),
  });

  const [daRimuovere, setDaRimuovere] = useState<{ userId: string; name: string } | null>(null);

  const cambiaRuolo = useMutation({
    mutationFn: (v: { userId: string; role: Ruolo }) =>
      must(api.PATCH('/api/v1/orgs/{slug}/members/{userId}', {
        params: { path: { slug, userId: v.userId } }, body: { role: v.role },
      })),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['members', slug] });
      avvisa(t('Ruolo aggiornato: {ruolo}', { ruolo: nomeRuolo(v.role) }));
    },
  });

  const rimuovi = useMutation({
    mutationFn: (userId: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/members/{userId}', {
        params: { path: { slug, userId } },
      })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', slug] });
      avvisa(t('{nome} non fa più parte dell’organizzazione', { nome: daRimuovere?.name ?? t('La persona') }));
      setDaRimuovere(null);
    },
  });

  const lista = membri.data?.members ?? [];

  return (
    <>
      <TopAppBar title={t('Persone')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={membri.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {membri.error && (
            <Banner tone="error" title={t('Non riesco a leggere le persone')}>{membri.error.message}</Banner>
          )}

          {amministra && <Accessi slug={slug} membri={membri.data?.members ?? []} />}

          {/* Il rifiuto dell'hub si mostra qui e non accanto alla riga: è la
              stessa regola per tutte — l'ultimo owner non si può togliere. */}
          {cambiaRuolo.error && (
            <Banner tone="error" title={t('Ruolo non cambiato')}>{cambiaRuolo.error.message}</Banner>
          )}

          {lista.length > 0 && (
            <DataTable
              columns={[
                { key: 'nome', header: t('Persona') },
                { key: 'ruolo', header: t('Ruolo') },
                { key: 'da', header: t('Da'), nowrap: true },
                { key: 'azioni', header: '' },
              ]}
              rowKey={(r) => String(r.id)}
              rows={lista.map((m) => ({
                id: m.userId,
                nome: (
                  <div className="md-stack__inline" style={{ alignItems: 'center', gap: 'var(--md-sys-spacing-3)' }}>
                    <Avatar name={m.name || m.email} />
                    <div className="md-due-righe">
                      <span className="md-body-large">
                        {m.disabled ? <s>{m.name}</s> : m.name}
                        {m.userId === me.data?.id && <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}> {t('(tu)')}</span>}
                      </span>
                      <span>{m.email}</span>
                    </div>
                  </div>
                ),
                ruolo: amministra ? (
                  <Select
                    label=""
                    aria-label={t('Ruolo di {nome}', { nome: m.name })}
                    value={m.role}
                    options={opzioniRuolo()}
                    disabled={cambiaRuolo.isPending}
                    onChange={(e) => cambiaRuolo.mutate({ userId: m.userId, role: e.target.value as Ruolo })}
                  />
                ) : (
                  <Chip kind="status">{nomeRuolo(m.role)}</Chip>
                ),
                da: quandoRelativo(m.since),
                azioni: amministra && m.userId !== me.data?.id ? (
                  <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                    <Button variant="text" onClick={() => setDaRimuovere({ userId: m.userId, name: m.name })}>
                      {t('Rimuovi')}
                    </Button>
                  </div>
                ) : null,
              }))}
            />
          )}

          {puoInvitare && <Inviti slug={slug} />}
        </div>
      </div>

      <Dialog
        open={daRimuovere !== null}
        onOpenChange={(v) => !v && setDaRimuovere(null)}
        title={t('Rimuovere {nome}?', { nome: daRimuovere?.name ?? '' })}
        description={t("Non vedrà più server, istanze e backup di questa organizzazione. L'account resta, e la puoi invitare di nuovo.")}
        actions={
          <>
            <Button variant="text" onClick={() => setDaRimuovere(null)}>{t('Annulla')}</Button>
            <Button
              variant="danger"
              icon={rimuovi.isPending ? <Anello /> : undefined}
              disabled={rimuovi.isPending}
              onClick={() => daRimuovere && rimuovi.mutate(daRimuovere.userId)}
            >
              {rimuovi.isPending ? t('Rimuovo…') : t('Rimuovi')}
            </Button>
          </>
        }
      >
        {rimuovi.error && (
          <Banner tone="error" title={t('Non rimossa')}>{rimuovi.error.message}</Banner>
        )}
      </Dialog>
    </>
  );
}

/**
 * Inviti pendenti e creazione.
 *
 * Il token torna **una sola volta**: in tabella resta solo il suo SHA-256.
 * Perciò si mostra come il comando di bootstrap di un server — grande, da
 * copiare — e non come una colonna dell'elenco, dove non potrebbe esserci.
 */
function Inviti({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [email, setEmail] = useState('');
  const [ruolo, setRuolo] = useState<Ruolo>('member');
  const [appenaCreato, setAppenaCreato] = useState<{ email: string; token?: string } | null>(null);

  const pendenti = useQuery({
    queryKey: ['invitations', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/invitations', { params: { path: { slug } } })),
  });

  const invita = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/invitations', {
        params: { path: { slug } }, body: { email: email.trim().toLowerCase(), role: ruolo },
      })),
    onSuccess: (inv) => {
      setAppenaCreato({ email: inv.email, token: inv.token });
      setEmail('');
      qc.invalidateQueries({ queryKey: ['invitations', slug] });
    },
  });

  const revoca = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/invitations/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations', slug] });
      avvisa(t('Invito annullato'));
    },
  });

  const lista = pendenti.data?.invitations ?? [];
  const emailValida = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  // Il token va nel fragment, non nella query: dopo il `#` il browser non lo
  // manda mai al server, quindi non finisce nei log di accesso di Caddy — da
  // dove chi ha accesso alla macchina potrebbe leggerlo e accettare l'invito
  // al posto del destinatario.
  const link = appenaCreato ? `${location.origin}/invito#token=${appenaCreato.token ?? ''}` : '';

  const copia = () => {
    void navigator.clipboard.writeText(link);
    avvisa(t('Link copiato: mandalo alla persona invitata'));
  };

  return (
    <Card variant="outlined">
      <div className="md-form">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Invita una persona')}</h2>

        {appenaCreato && (
          <Banner
            tone="success"
            title={t('Invito pronto per {email}', { email: appenaCreato.email })}
            action={<Button variant="text" onClick={() => setAppenaCreato(null)}>{t('Fatto')}</Button>}
          >
            <div className="md-stack" style={{ marginTop: 'var(--md-sys-spacing-2)' }}>
              <span>
                {t('Mandale questo link: vale sette giorni e {soloAdesso}. Molo non spedisce email.', {
                  soloAdesso: <strong>{t('lo vedi solo adesso')}</strong>,
                })}
              </span>
              <div className="md-copia">
                <TextField label="" aria-label={t("Link dell'invito")} readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
                <Button variant="outlined" icon={<Copy />} onClick={copia}>{t('Copia')}</Button>
              </div>
            </div>
          </Banner>
        )}

        <form
          className="md-form__row"
          onSubmit={(e) => { e.preventDefault(); if (emailValida) invita.mutate(); }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('collega@meshble.com')}
            help={t("L'invito vale solo per questo indirizzo.")}
          />
          <Select
            label={t('Ruolo')}
            value={ruolo}
            options={opzioniRuolo()}
            onChange={(e) => setRuolo(e.target.value as Ruolo)}
            help={spiegaRuolo(ruolo)}
          />
          <Button type="submit" variant="filled" icon={invita.isPending ? <Anello /> : undefined} disabled={!emailValida || invita.isPending} style={{ marginTop: 22 }}>
            {invita.isPending ? t('Invito…') : t('Invita')}
          </Button>
        </form>

        {invita.error && <Banner tone="error" title={t('Invito non creato')}>{invita.error.message}</Banner>}

        {lista.length > 0 && (
          <>
            <h3 className="md-title-small" style={{ margin: 0 }}>{t('In attesa di risposta')}</h3>
            <DataTable
              columns={[
                { key: 'email', header: t('Invitata') },
                { key: 'ruolo', header: t('Ruolo') },
                { key: 'scade', header: t('Scade') },
                { key: 'azioni', header: '' },
              ]}
              rowKey={(r) => String(r.id)}
              rows={lista.map((i) => ({
                id: i.id,
                email: i.email,
                ruolo: <Chip kind="status">{nomeRuolo(i.role)}</Chip>,
                scade: new Date(i.expiresAt).toLocaleDateString(localeIntl(), { day: 'numeric', month: 'long' }),
                azioni: (
                  <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                    <Button variant="text" disabled={revoca.isPending} onClick={() => revoca.mutate(i.id)}>
                      {t('Annulla invito')}
                    </Button>
                  </div>
                ),
              }))}
            />
          </>
        )}
      </div>
    </Card>
  );
}


/**
 * Gli accessi per risorsa.
 *
 * È come si dà a un cliente l'accesso alla sola sua istanza: lo si invita
 * come «Limitato», che da solo non vede niente, e gli si concede quella. Il
 * ruolo del grant vale solo lì e su ciò che le sta sotto — un grant su un
 * server copre le sue istanze.
 */
function Accessi({ slug, membri }: { slug: string; membri: Array<{ userId: string; email: string; role: string }> }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();

  const grants = useQuery({
    queryKey: ['grants', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/grants', { params: { path: { slug } } })),
  });
  const servers = useQuery({
    queryKey: ['servers', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/servers', { params: { path: { slug } } })),
  });
  const istanze = useQuery({
    queryKey: ['instances', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/instances', { params: { path: { slug } } })),
  });

  const [apri, setApri] = useState(false);
  const [userId, setUserId] = useState('');
  const [risorsa, setRisorsa] = useState('');
  const [role, setRole] = useState<'viewer' | 'operator' | 'admin'>('operator');

  const invalida = () => qc.invalidateQueries({ queryKey: ['grants', slug] });

  // Le risorse concedibili, server e istanze insieme: chi concede pensa «la
  // sua istanza», non «un oggetto di tipo istanza».
  const risorse = [
    ...(servers.data?.servers ?? []).map((s) => ({ id: s.id, kind: 'server' as const, label: t('Server {nome}', { nome: s.name }) })),
    ...(istanze.data?.instances ?? []).map((i) => ({ id: i.id, kind: 'instance' as const, label: t('Istanza {nome}', { nome: i.name }) })),
  ];
  const scelta = risorse.find((r) => r.id === risorsa);

  const concedi = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/grants', {
        params: { path: { slug } },
        body: { userId, kind: scelta!.kind, resourceId: risorsa, role },
      })),
    onSuccess: () => {
      invalida();
      setApri(false);
      setRisorsa('');
      avvisa(t('Accesso concesso'));
    },
  });

  const revoca = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/grants/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      invalida();
      avvisa(t('Accesso revocato'));
    },
  });

  const elenco = grants.data ?? [];
  const pronto = userId !== '' && scelta !== undefined && !concedi.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Accessi a singole risorse')}</h2>
        <Button variant="tonal" disabled={risorse.length === 0} onClick={() => setApri(true)}>
          {t('Concedi un accesso')}
        </Button>
      </div>

      {elenco.length === 0 ? (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Nessuno. Serve a dare a una persona una macchina o un’istanza sola: la si invita come «Limitato», che da solo non vede niente, e le si concede quella.')}
        </p>
      ) : (
        <List>
          {elenco.map((g) => (
            <ListItem
              key={g.id}
              headline={`${g.email} → ${g.targetName || g.target}`}
              supporting={
                g.targetName
                  ? g.kind === 'server'
                    ? t('{ruolo} su il server e le sue istanze', { ruolo: g.role })
                    : t('{ruolo} su quell’istanza', { ruolo: g.role })
                  : t('{ruolo} · la risorsa non esiste più: l’accesso va tolto', { ruolo: g.role })
              }
              trailing={
                <Button variant="text" disabled={revoca.isPending} onClick={() => revoca.mutate(g.id)}>
                  {t('Revoca')}
                </Button>
              }
            />
          ))}
        </List>
      )}

      {(concedi.error || revoca.error) && (
        <Banner tone="error" title={t('Non ha funzionato')}>
          {((concedi.error ?? revoca.error) as Error).message}
        </Banner>
      )}

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Concedi un accesso')}
        description={t('Il ruolo vale solo su quella risorsa e su ciò che le sta sotto.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button variant="filled" disabled={!pronto} onClick={() => concedi.mutate()}>{t('Concedi')}</Button>
          </>
        }
      >
        <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (pronto) concedi.mutate(); }}>
          <Select
            label={t('A chi')}
            value={userId}
            options={[{ value: '', label: t('Scegli una persona') },
              ...membri.map((m) => ({ value: m.userId, label: `${m.email} (${nomeRuolo(m.role)})` }))]}
            onChange={(e) => setUserId(e.target.value)}
          />
          <Select
            label={t('Su cosa')}
            value={risorsa}
            options={[{ value: '', label: t('Scegli una risorsa') },
              ...risorse.map((r) => ({ value: r.id, label: r.label }))]}
            onChange={(e) => setRisorsa(e.target.value)}
          />
          <Select
            label={t('Con che ruolo')}
            value={role}
            options={[
              { value: 'viewer', label: t('Viewer — guarda e basta') },
              { value: 'operator', label: t('Operator — avvia, ferma, backup, domini') },
              { value: 'admin', label: t('Admin della risorsa — anche cancellarla') },
            ]}
            onChange={(e) => setRole(e.target.value as 'viewer' | 'operator' | 'admin')}
          />
        </form>
      </Dialog>
    </Card>
  );
}
